import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'

export default function Services({ role }) {
  const [services, setServices] = useState([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [duration, setDuration] = useState('')
  const [price, setPrice] = useState('')
  const [category, setCategory] = useState('Hair')
  const [saving, setSaving] = useState(false)

  const categories = ['Hair', 'Color', 'Treatment', 'Special']

  useEffect(() => { fetchServices() }, [])

  async function fetchServices() {
    const { data } = await supabase
      .from('services')
      .select('*')
      .order('category')
    if (data) setServices(data)
    setLoading(false)
  }

  function openAdd() {
    setEditing(null)
    setName(''); setDescription(''); setDuration(''); setPrice(''); setCategory('Hair')
    setShowModal(true)
  }

  function openEdit(service) {
    setEditing(service)
    setName(service.name)
    setDescription(service.description || '')
    setDuration(service.duration.toString())
    setPrice(service.price.toString())
    setCategory(service.category)
    setShowModal(true)
  }

  async function save() {
    if (!name || !duration || !price) return
    setSaving(true)
    const data = { name, description, duration: parseInt(duration), price: parseFloat(price), category }
    if (editing) {
      await supabase.from('services').update(data).eq('id', editing.id)
    } else {
      await supabase.from('services').insert({ ...data, active: true })
    }
    setSaving(false)
    setShowModal(false)
    fetchServices()
  }

  async function toggleActive(service) {
    await supabase.from('services').update({ active: !service.active }).eq('id', service.id)
    fetchServices()
  }

  async function deleteService(service) {
    if (window.confirm(`Delete "${service.name}"? This cannot be undone.`)) {
      await supabase.from('services').delete().eq('id', service.id)
      fetchServices()
    }
  }

  const grouped = categories.reduce((acc, cat) => {
    acc[cat] = services.filter(s => s.category === cat)
    return acc
  }, {})

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>Services</h1>
        {(role === 'owner' || role === 'admin') && (
          <button style={styles.addBtn} onClick={openAdd}>+ Add Service</button>
        )}
      </div>

      {loading ? <div style={styles.empty}>Loading...</div> : (
        categories.map(cat => grouped[cat]?.length > 0 && (
          <div key={cat} style={styles.categorySection}>
            <div style={styles.categoryTitle}>{cat.toUpperCase()}</div>
            <div style={styles.serviceGrid}>
              {grouped[cat].map(service => (
                <div key={service.id} style={{
                  ...styles.serviceCard,
                  opacity: service.active ? 1 : 0.5,
                }}>
                  <div style={styles.serviceHeader}>
                    <div style={styles.serviceName}>{service.name}</div>
                    <div style={styles.servicePrice}>${service.price}</div>
                  </div>
                  <div style={styles.serviceDesc}>{service.description}</div>
                  <div style={styles.serviceDuration}>{service.duration} mins</div>
                  {(role === 'owner' || role === 'admin') && (
                    <div style={styles.serviceActions}>
                      <button style={styles.actionBtn} onClick={() => openEdit(service)}>Edit</button>
                      <button style={styles.actionBtn} onClick={() => toggleActive(service)}>
                        {service.active ? 'Hide' : 'Show'}
                      </button>
                      <button style={styles.deleteBtn} onClick={() => deleteService(service)}>Delete</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))
      )}

      {showModal && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>{editing ? 'Edit Service' : 'Add Service'}</h2>
              <button style={styles.closeBtn} onClick={() => setShowModal(false)}>✕</button>
            </div>

            <label style={styles.label}>Name *</label>
            <input style={styles.input} value={name} onChange={e => setName(e.target.value)} placeholder="Service name" />

            <label style={styles.label}>Description</label>
            <textarea style={styles.textarea} value={description} onChange={e => setDescription(e.target.value)} rows={3} />

            <div style={{ display: 'flex', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Duration (mins) *</label>
                <input style={styles.input} value={duration} onChange={e => setDuration(e.target.value)} type="number" />
              </div>
              <div style={{ flex: 1 }}>
                <label style={styles.label}>Price ($) *</label>
                <input style={styles.input} value={price} onChange={e => setPrice(e.target.value)} type="number" />
              </div>
            </div>

            <label style={styles.label}>Category *</label>
            <div style={styles.pillRow}>
              {categories.map(cat => (
                <button
                  key={cat}
                  style={{ ...styles.pill, ...(category === cat ? styles.pillActive : {}) }}
                  onClick={() => setCategory(cat)}>
                  {cat}
                </button>
              ))}
            </div>

            <button style={styles.saveBtn} onClick={save} disabled={saving}>
              {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Service'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { padding: '32px', maxWidth: '1200px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  title: {
    fontSize: '28px', fontWeight: '600', color: '#D4AF77',
    letterSpacing: '2px', textTransform: 'uppercase', fontFamily: 'Cormorant Garamond, serif',
  },
  addBtn: {
    padding: '10px 20px', backgroundColor: '#D4AF77', color: '#000',
    border: 'none', borderRadius: '2px', fontSize: '11px', fontWeight: '700',
    letterSpacing: '1.5px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  },
  categorySection: { marginBottom: '32px' },
  categoryTitle: {
    fontSize: '10px', fontWeight: '600', color: '#555',
    letterSpacing: '2px', marginBottom: '12px',
  },
  serviceGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' },
  serviceCard: {
    backgroundColor: '#111', border: '1px solid #1a1a1a',
    borderRadius: '2px', padding: '16px',
  },
  serviceHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '6px' },
  serviceName: { fontSize: '15px', fontWeight: '600', color: '#F5F0E8' },
  servicePrice: { fontSize: '16px', fontWeight: '600', color: '#D4AF77' },
  serviceDesc: { fontSize: '12px', color: '#555', marginBottom: '6px', lineHeight: '1.5' },
  serviceDuration: { fontSize: '11px', color: '#444', letterSpacing: '0.5px', marginBottom: '12px' },
  serviceActions: { display: 'flex', gap: '8px', borderTop: '1px solid #1a1a1a', paddingTop: '12px' },
  actionBtn: {
    flex: 1, padding: '6px', background: '#1a1a1a', border: 'none',
    color: '#888', fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  deleteBtn: {
    flex: 1, padding: '6px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)',
    color: '#ef4444', fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  overlay: {
    position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.8)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modal: {
    backgroundColor: '#111', border: '1px solid #222', borderRadius: '2px',
    padding: '32px', width: '100%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto',
  },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#F5F0E8', letterSpacing: '0.5px' },
  closeBtn: {
    background: 'none', border: 'none', color: '#555', fontSize: '18px',
    cursor: 'pointer', padding: '4px',
  },
  label: {
    display: 'block', fontSize: '10px', fontWeight: '600', color: '#555',
    letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '6px', marginTop: '16px',
    fontFamily: 'Montserrat, sans-serif',
  },
  input: {
    width: '100%', padding: '12px', backgroundColor: '#0a0a0a', border: '1px solid #222',
    borderRadius: '2px', color: '#F5F0E8', fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif', outline: 'none', boxSizing: 'border-box',
  },
  textarea: {
    width: '100%', padding: '12px', backgroundColor: '#0a0a0a', border: '1px solid #222',
    borderRadius: '2px', color: '#F5F0E8', fontSize: '14px',
    fontFamily: 'Montserrat, sans-serif', outline: 'none', resize: 'vertical', boxSizing: 'border-box',
  },
  pillRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  pill: {
    padding: '8px 16px', background: 'none', border: '1px solid #222',
    color: '#555', fontSize: '11px', fontWeight: '600', letterSpacing: '1px',
    cursor: 'pointer', borderRadius: '2px', fontFamily: 'Montserrat, sans-serif',
  },
  pillActive: { backgroundColor: '#D4AF77', borderColor: '#D4AF77', color: '#000' },
  saveBtn: {
    marginTop: '24px', width: '100%', padding: '14px', backgroundColor: '#D4AF77',
    color: '#000', border: 'none', borderRadius: '2px', fontSize: '11px', fontWeight: '700',
    letterSpacing: '2px', cursor: 'pointer', fontFamily: 'Montserrat, sans-serif',
  },
  empty: { color: '#444', fontSize: '14px', padding: '24px', textAlign: 'center' },
}